import { Centered, EmptyState, FlexColumn, MaterialIcon, useMoment } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { Spinner } from 'baseui/spinner';
import { ParagraphSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { PATHS_TABLE_COLUMNS } from '../../../constants/paths';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { getPaths } from '../../../services/reportsService';
import { apiParamsState, isAllWorkspacesSelect } from '../../../storage/app';
import { assetState } from '../../../storage/assets';
import { currentStateState } from '../../../storage/client';
import {
  activePathState,
  pathDistanceFilterState,
  pathsState,
  pathTimeFilterState,
  reportEndDateState,
  reportStartDateState,
  selectedPathIdState,
} from '../../../storage/reports';
import { isVehicleKind } from '../../../utils/assets';
import { isDevice } from '../../../utils/currentStates';
import { formatDurationFromSeconds } from '../../../utils/dates';
import { StyledPaginatedTable } from '../../shared/StyledPaginatedTable';
import VehicleData from '../../shared/VehicleData';
import { StyledTooltip } from '../../ui/StyledTooltip';

type Props = Readonly<{
  assetId: string;
}>;

function PathItem({ assetId }: Props) {
  const apiParams = useRecoilValue(apiParamsState);
  const [paths, setPaths] = useRecoilState(pathsState);
  const [selectedPathId, setSelectedPathId] = useRecoilState(selectedPathIdState);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const { formatDateFromTs } = useMoment();
  const [loading, setLoading] = useState(false);
  const asset = useRecoilValue(assetState(assetId));
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);
  const endDate = useRecoilValue(reportEndDateState);
  const startDate = useRecoilValue(reportStartDateState);
  const activePath = useRecoilValue(activePathState);
  const isActive = assetId === activePath;
  const pathTimeFilter = useRecoilValue(pathTimeFilterState);
  const pathDistanceFilter = useRecoilValue(pathDistanceFilterState);
  const currentState = useRecoilValue(currentStateState(isDevice(asset?.device) ? asset.device.ident : ''));

  useUpdateEffect(() => {
    setPage(0);
  }, [
    pathTimeFilter,
    pathDistanceFilter,
    assetId,
    startDate,
    endDate,
    isActive,
    apiParams?.clientId,
    apiParams?.subClientId,
    isAllWorkspacesSelected,
  ]);

  useEffect(() => {
    if ((startDate && !endDate) || (endDate && !startDate) || !isActive) {
      setPaths(undefined);
      setTotalItems(0);
      return;
    }
    if (page === 0) {
      setPage(1);
      return;
    }
    setLoading(true);
    const loadPaths = async () => {
      if (!apiParams) return;
      const { error, paths, total } = await getPaths(
        apiParams.clientId,
        assetId,
        page,
        isAllWorkspacesSelected ? undefined : apiParams.subClientId,
        startDate,
        endDate,
        pathTimeFilter * 60 || 300,
        pathDistanceFilter || 700,
      );
      if (!error && paths && total) {
        setPaths(paths);
        setTotalItems(total);
      } else if (error) {
        setPaths(undefined);
        setTotalItems(0);
      }
      setLoading(false);
    };
    loadPaths();

    return () => {
      setPaths(undefined);
    };
  }, [page]);

  if (!isActive) {
    return null;
  }

  return (
    <FlexColumn>
      {!paths?.length && loading && (
        <Centered>
          <Spinner />
        </Centered>
      )}
      {!loading && asset && (
        <>
          {isVehicleKind(asset.assetType) && (
            <VehicleData
              vehicle={asset.vehicle}
              ident={isDevice(asset.device) ? asset.device.ident : undefined}
              odometer={currentState?.gnss_vehicle_mileage}
              simpleMode
              dataOnly
              hiddenUnit
              hiddenHourometer
            />
          )}
        </>
      )}
      {!paths?.length && !loading && <EmptyState title="Todavía no cuenta con trayectos este activo." />}
      {!!paths?.length && (
        <StyledPaginatedTable
          columns={PATHS_TABLE_COLUMNS}
          activeId={selectedPathId}
          data={paths.map((path) => {
            return {
              id: path._id,
              items: [
                formatDateFromTs(path.startDate),
                formatDateFromTs(path.endDate),
                <FlexColumn
                  gap={0}
                  key={`info-${path._id}`}
                >
                  <ParagraphSmall margin={0}>{path.distanceKms?.toFixed(2)} Km</ParagraphSmall>
                  <ParagraphSmall margin={0}>{formatDurationFromSeconds(path.totalSeconds)}</ParagraphSmall>
                </FlexColumn>,
                <StyledTooltip
                  key={`details-${path._id}`}
                  content={'Ver trayecto'}
                  showArrow={false}
                >
                  <Button
                    size="compact"
                    shape="square"
                    onClick={() => setSelectedPathId(path._id)}
                    kind="tertiary"
                  >
                    <MaterialIcon name="timeline" />
                  </Button>
                </StyledTooltip>,
              ],
            };
          })}
          totalItems={totalItems}
          page={page}
          onPageChange={setPage}
          minTableWidthSize={480}
        />
      )}
    </FlexColumn>
  );
}

export default PathItem;
